import React from 'react';
import moment from 'moment-timezone';
import { useIntl } from 'react-intl';
import { currencyFormatter, customNumberFormatString } from '../functions/general';
import { SimpleMerchant } from '../interfaces/Merchant';
import { SimplePipeline } from '../interfaces/Pipeline';
import { SimpleMeeting } from '../interfaces/Meeting';
import { SimpleProduct } from '../interfaces/Product';

interface HandleChangeValueParams {
    key: string;
    value: any;
    type?: string;
    user?: any;
    intl?: any;
    simpleMerchants?: SimpleMerchant[];
    simplePipelines?: SimplePipeline[];
    simpleMeetings?: SimpleMeeting[];
    simpleProducts?: SimpleProduct[];
    onAttachmentClick?: (attachmentUrl: string | string[], attachmentKey?: string, title?: string) => void;
}

export const handleChangeValue = ({
    key,
    value,
    type,
    user,
    intl,
    simpleMerchants = [],
    simplePipelines = [],
    simpleMeetings = [],
    simpleProducts = [],
    onAttachmentClick
}: HandleChangeValueParams) => {
    if (key === 'totalNominalPipeline') {
        return (
            <span style={{ fontWeight: "700", color: "black" }}>
                {currencyFormatter(value, user?.data?.companyCurrencyNeedRound, user?.data?.companyCurrency, { showPrefix: true })}
            </span>
        );
    }

    if (!type) {
        return value || "-";
    }

    switch (type) {
        case 'merchant':
            const merchantData = simpleMerchants.filter((itemMerchant: SimpleMerchant) => 
                itemMerchant?.id && value?.includes(itemMerchant.id)
            );
            if (merchantData.length > 0) {
                return merchantData.map((item: SimpleMerchant, index: number) => (
                    <div key={item.id} className="d-flex flex-column">
                        <span><b>{item.name}</b> / </span>
                        <span>{item.phone} {index !== merchantData.length - 1 && ','}</span>
                    </div>
                ));
            }
            return '-';

        case 'pipeline':
            const pipelineData = simplePipelines.find((item: SimplePipeline) => item.id === value);
            if (pipelineData) {
                return (
                    <div className="d-flex flex-column">
                        <span><b>{pipelineData.name}</b> / </span>
                        <span>{currencyFormatter(pipelineData.nominal, user?.data?.companyCurrencyNeedRound, user?.data?.companyCurrency as any, { showPrefix: true })} / </span>
                        <span>{moment(pipelineData.dateCreated).format("DD MMM YYYY")}</span>
                    </div>
                );
            }
            return '-';

        case 'meeting':
            const meetingData = simpleMeetings.filter((item: SimpleMeeting) => 
                item.id && value?.includes(item.id)
            );
            if (meetingData.length > 0) {
                return meetingData.map((item: SimpleMeeting, index: number) => (
                    <div key={item.id} className="d-flex flex-column">
                        <span><b>{item.name}</b> / </span>
                        <span>{moment(item.dateMeet).format('DD MMM YYYY')} {index !== meetingData.length - 1 && ','}</span>
                    </div>
                ));
            }
            return '-';

        case 'product':
            const productData = simpleProducts.filter((item: SimpleProduct) => 
                item.id && value?.includes(item.id)
            );
            if (productData.length > 0) {
                return productData.map((item: SimpleProduct, index: number) => (
                    <div key={item.id} className="d-flex flex-column">
                        <span><b>{item.name}</b> {index !== productData.length - 1 && ','}</span>
                    </div>
                ));
            }
            return '-';

        case 'text':
        case 'option':
        case 'multiple':
        case 'multipleOption':
            return value ? value.toString().split(',').join(', ') : '-';

        case 'date':
            return value ? moment(value).format("DD MMM YYYY") : '-';

        case 'dateTimeRange':
            const convertValues = Array.isArray(value) ? 
                value.map((item: any) => moment(item).format("DD MMM YYYY HH:mm")) : null;
            return convertValues ? convertValues.join(' - ') : '-';

        case 'image':
        case 'images':
            return value ? (
                <span 
                    role="button" 
                    onClick={() => onAttachmentClick?.(value, key, key)}
                    style={{ cursor: 'pointer' }}
                >
                    <i className="fas fs-2 fa-eye"></i>
                </span>
            ) : '-';

        case 'file':
        case 'files':
            if (typeof value === "string") {
                return value ? (
                    <a
                        href={value}
                        target="_blank"
                        rel="noreferrer"
                        className="ms-2 btn btn-sm btn-icon btn-bg-light btn-active-color-primary"
                    >
                        <i className="fas fa-file-invoice fs-3"></i>
                    </a>
                ) : '-';
            } else {
                return value ? (
                    <span 
                        role="button" 
                        onClick={() => onAttachmentClick?.(value, key, key)}
                        style={{ cursor: 'pointer' }}
                    >
                        <i className="fas fa-file-invoice fs-3"></i>
                    </span>
                ) : '-';
            }

        case 'serial':
            return typeof value === 'string' ? value : 
                (value as string[])?.length > 0 ? (value as string[]).join('') : '-';

        case 'link':
            return value ? (
                <a 
                    href={value} 
                    target="_blank" 
                    rel="noreferrer"
                    className="btn btn-sm btn-outline btn-outline-default btn-active-light text-gray-600"
                >
                    <i className="fas fa-external-link-alt fs-6 ms-2 p-0 me-2"></i>
                    {intl?.formatMessage?.({ id: 'FORM.LABEL.LOOK' }) || 'Look'}
                </a>
            ) : '-';

        case 'phone':
            return value ? value : '-';

        case 'number':
            return value ? customNumberFormatString(value.toString()) : '-';

        case 'time':
            return value ? moment(value).format("HH:mm") : '-';

        case 'dateTime':
            return value ? moment(value).format("DD MMM YYYY HH:mm") : '-';

        default:
            return value || "-";
    }
};

export const handleChangeKeyName = (key: string) => {
    switch (key) {
        case 'merchantName':
            return 'Nama Merchant';
        case 'totalNominalPipeline':
            return 'Total Nominal Pipeline';
        case 'merchantTemplateName':
            return 'Nama Template Merchant';
        default:
            return key;
    }
}; 